import { useState, useEffect } from 'react'
import { Card, Row, Col, Form, Button, Spinner, Alert, Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import { formatCurrency } from '../../utils/formatters' // Asumiendo un formateador de moneda
import { startOfLocalDayFromApi } from '../../utils/apiDateFormat'

const EstadoResultadosReport = ({ onDrillDown }) => {
  const { clientId: clienteId, setClientId: setClienteId, selectDisabled } = useSyncedLocalClientId()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [exporting, setExporting] = useState(false)

  const handleConceptClick = (conceptType) => {
    if (!onDrillDown) return
    
    // Mapear tipos de concepto a tipos de cuenta
    const accountTypeMap = {
      'ingresos': 'INGRESO',
      'costos': 'COSTO',
      'gastos_admin': 'GASTO', // Se puede refinar después
      'gastos_ventas': 'GASTO', // Se puede refinar después
      'otros_gastos_op': 'GASTO', // Se puede refinar después
      'ingresos_financieros': 'INGRESO', // Código empieza con '7'
      'gastos_financieros': 'GASTO', // Código empieza con '7' o '8'
      'otros_ingresos': 'INGRESO', // Código empieza con '7'
      'otros_egresos': 'GASTO' // Código empieza con '8'
    }
    
    const accountType = accountTypeMap[conceptType]
    if (accountType) {
      onDrillDown({
        clientId: clienteId,
        accountType: accountType,
        startDate: startDate,
        endDate: endDate,
        fromReport: 'Estado de Resultados',
        fromReportTab: 'estado-resultados'
      }, 'transactions')
    }
  }

  const handleExport = async (format) => {
    try {
      setExporting(true)
      const params = new URLSearchParams()
      if (clienteId) params.append('client_id', clienteId)
      if (startDate) params.append('fecha_inicio', startDate)
      if (endDate) params.append('fecha_fin', endDate)
      params.append('format', format)
      
      const response = await api.get(`/financial/reportes/exportar/estado-resultados/?${params.toString()}`, {
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Estado_Resultados.${format === 'excel' ? 'xlsx' : 'pdf'}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error('Error exportando:', error)
    } finally {
      setExporting(false)
    }
  }

  // Obtener lista de clientes para el filtro
  const { data: clientesData, isLoading: isLoadingClients } = useQuery(
    'clients',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    }
  )
  const clients = Array.isArray(clientesData) ? clientesData : []

  // Obtener datos del Estado de Resultados
  const { data: reportData, isLoading: isLoadingReport, error: reportError, refetch } = useQuery(
    ['estado-resultados-report', clienteId, startDate, endDate],
    async () => {
      const params = new URLSearchParams()
      if (clienteId) params.append('client_id', clienteId)
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)
      const response = await api.get(`/financial/reportes/estado-resultados/?${params.toString()}`)
      return response.data
    },
    { 
      enabled: !!startDate && !!endDate, // Solo habilitar la consulta si las fechas están presentes
      onError: (err) => {
        console.error("Error al cargar Estado de Resultados", err)
      }
    }
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Validar fechas
    if (startDate && endDate) {
      const start = startOfLocalDayFromApi(startDate)
      const end = startOfLocalDayFromApi(endDate)
      if (start && end && start > end) {
        toast.error('La fecha de inicio debe ser anterior a la fecha de fin.')
        return
      }
    }
    
    if (!startDate || !endDate) {
      toast.error('Por favor, seleccione ambas fechas.')
      return
    }
    
    refetch()
  }

  // Shortcuts de teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + Enter para generar reporte
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        const form = e.target.closest('form')
        if (form) {
          const submitButton = form.querySelector('button[type="submit"]')
          if (submitButton && !submitButton.disabled) {
            submitButton.click()
          }
        }
      }
      // Ctrl/Cmd + S para exportar a Excel
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (reportData && !exporting) {
          handleExport('excel')
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [reportData, exporting]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoadingClients) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando clientes...</p>
      </div>
    )
  }

  return (
    <div>
      <Card className="mb-4">
        <Card.Header as="h5">Estado de Resultados</Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col xs={12} md={4}>
                <Form.Group controlId="formClient">
                  <Form.Label>Cliente</Form.Label>
                  <Form.Select
                    value={clienteId}
                    onChange={(e) => setClienteId(e.target.value)}
                    disabled={selectDisabled}
                  >
                    <option value="">Todos los clientes</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.razon_social || client.business_name || `${client.nombre} ${client.apellido_paterno || ''}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group controlId="formStartDate">
                  <Form.Label>
                    Fecha Inicio
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          Seleccione la fecha de inicio del período para el Estado de Resultados
                        </Tooltip>
                      }
                    >
                      <i className="bi bi-info-circle ms-2 text-info" style={{ cursor: 'help' }}></i>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                    max={endDate || undefined}
                  />
                  {startDate && endDate && new Date(startDate) > new Date(endDate) && (
                    <Form.Text className="text-danger">
                      La fecha de inicio debe ser anterior a la fecha de fin.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group controlId="formEndDate">
                  <Form.Label>
                    Fecha Fin
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          Seleccione la fecha de fin del período para el Estado de Resultados
                        </Tooltip>
                      }
                    >
                      <i className="bi bi-info-circle ms-2 text-info" style={{ cursor: 'help' }}></i>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                    min={startDate || undefined}
                  />
                  {startDate && endDate && startOfLocalDayFromApi(startDate) > startOfLocalDayFromApi(endDate) && (
                    <Form.Text className="text-danger">
                      La fecha de fin debe ser posterior a la fecha de inicio.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} className="d-flex gap-2 flex-wrap">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Genera el Estado de Resultados con los filtros seleccionados (Ctrl+Enter)</Tooltip>}
                >
                  <Button variant="primary" type="submit" disabled={isLoadingReport || !startDate || !endDate} className="flex-grow-1 flex-md-grow-0">
                    {isLoadingReport ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-file-earmark-text me-2"></i>
                        Generar Reporte
                      </>
                    )}
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Exporta el reporte actual a formato PDF</Tooltip>}
                >
                  <span>
                    <Button variant="outline-danger" onClick={() => handleExport('pdf')} disabled={!reportData || exporting || isLoadingReport} className="flex-grow-1 flex-md-grow-0">
                      {exporting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Exportando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-file-earmark-pdf me-2"></i>
                          <span className="d-none d-md-inline">Exportar </span>PDF
                        </>
                      )}
                    </Button>
                  </span>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Exporta el reporte actual a formato Excel (Ctrl+S)</Tooltip>}
                >
                  <span>
                    <Button variant="outline-success" onClick={() => handleExport('excel')} disabled={!reportData || exporting || isLoadingReport} className="flex-grow-1 flex-md-grow-0">
                      {exporting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Exportando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-file-earmark-excel me-2"></i>
                          <span className="d-none d-md-inline">Exportar </span>Excel
                        </>
                      )}
                    </Button>
                  </span>
                </OverlayTrigger>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {reportError && (
        <Alert variant="danger" dismissible>
          <Alert.Heading>Error al cargar el reporte</Alert.Heading>
          <p className="mb-0">
            {reportError.response?.data?.detail || reportError.response?.data?.error || reportError.message || 'Ocurrió un error al generar el Estado de Resultados. Por favor, intente nuevamente.'}
          </p>
          {reportError.response?.status && (
            <small className="text-muted">Código de error: {reportError.response.status}</small>
          )}
        </Alert>
      )}

      {!reportData && !isLoadingReport && startDate && endDate && (
        <Alert variant="info">
          <i className="bi bi-info-circle me-2"></i>
          Seleccione un rango de fechas y haga clic en &apos;Generar Reporte&apos; para ver el Estado de Resultados.
        </Alert>
      )}

      {isLoadingReport && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3 text-muted">Generando Estado de Resultados...</p>
        </div>
      )}

      {reportData && (
        <Card>
          <Card.Header as="h6">Detalle del Estado de Resultados</Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Concepto</th>
                  <th className="text-end">Monto</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {onDrillDown ? (
                      <a 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault()
                          handleConceptClick('ingresos')
                        }}
                        className="text-decoration-none"
                        title="Ver transacciones de ingresos"
                      >
                        <strong>Ingresos / Ventas Netas</strong>
                        <i className="ms-2 bi bi-arrow-right-circle" style={{ fontSize: '0.9em' }}></i>
                      </a>
                    ) : (
                      <strong>Ingresos / Ventas Netas</strong>
                    )}
                  </td>
                  <td className="text-end">{formatCurrency(reportData.ventas_netas)}</td>
                </tr>
                <tr>
                  <td>
                    {onDrillDown ? (
                      <a 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault()
                          handleConceptClick('costos')
                        }}
                        className="text-decoration-none"
                        title="Ver transacciones de costos"
                      >
                        Costos de Ventas
                        <i className="ms-2 bi bi-arrow-right-circle" style={{ fontSize: '0.9em' }}></i>
                      </a>
                    ) : (
                      'Costos de Ventas'
                    )}
                  </td>
                  <td className="text-end">{formatCurrency(reportData.costos_de_ventas)}</td>
                </tr>
                <tr>
                  <td><strong>Utilidad Bruta</strong></td>
                  <td className="text-end"><strong>{formatCurrency(reportData.utilidad_bruta)}</strong></td>
                </tr>
                <tr>
                  <td colSpan="2"><strong>Gastos Operacionales</strong></td>
                </tr>
                <tr>
                  <td>
                    <span style={{ paddingLeft: '1.5rem' }}>
                      {onDrillDown ? (
                        <a 
                          href="#" 
                          onClick={(e) => {
                            e.preventDefault()
                            handleConceptClick('gastos_admin')
                          }}
                          className="text-decoration-none"
                          title="Ver transacciones de gastos de administración"
                        >
                          Gastos de Administración
                          <i className="ms-2 bi bi-arrow-right-circle" style={{ fontSize: '0.9em' }}></i>
                        </a>
                      ) : (
                        'Gastos de Administración'
                      )}
                    </span>
                  </td>
                  <td className="text-end">{formatCurrency(reportData.gastos_operacionales.administracion)}</td>
                </tr>
                <tr>
                  <td>
                    <span style={{ paddingLeft: '1.5rem' }}>
                      {onDrillDown ? (
                        <a 
                          href="#" 
                          onClick={(e) => {
                            e.preventDefault()
                            handleConceptClick('gastos_ventas')
                          }}
                          className="text-decoration-none"
                          title="Ver transacciones de gastos de ventas"
                        >
                          Gastos de Ventas
                          <i className="ms-2 bi bi-arrow-right-circle" style={{ fontSize: '0.9em' }}></i>
                        </a>
                      ) : (
                        'Gastos de Ventas'
                      )}
                    </span>
                  </td>
                  <td className="text-end">{formatCurrency(reportData.gastos_operacionales.ventas)}</td>
                </tr>
                <tr>
                  <td>
                    <span style={{ paddingLeft: '1.5rem' }}>
                      {onDrillDown ? (
                        <a 
                          href="#" 
                          onClick={(e) => {
                            e.preventDefault()
                            handleConceptClick('otros_gastos_op')
                          }}
                          className="text-decoration-none"
                          title="Ver otros gastos operacionales"
                        >
                          Otros Gastos Operacionales
                          <i className="ms-2 bi bi-arrow-right-circle" style={{ fontSize: '0.9em' }}></i>
                        </a>
                      ) : (
                        'Otros Gastos Operacionales'
                      )}
                    </span>
                  </td>
                  <td className="text-end">{formatCurrency(reportData.gastos_operacionales.otros_gastos_operacionales)}</td>
                </tr>
                <tr>
                  <td><strong>Total Gastos Operacionales</strong></td>
                  <td className="text-end"><strong>{formatCurrency(reportData.gastos_operacionales.total)}</strong></td>
                </tr>
                <tr>
                  <td><strong>Utilidad Operacional</strong></td>
                  <td className="text-end"><strong>{formatCurrency(reportData.utilidad_operacional)}</strong></td>
                </tr>
                <tr>
                  <td colSpan="2"><strong>Otros Ingresos y Egresos</strong></td>
                </tr>
                <tr>
                  <td>
                    <span style={{ paddingLeft: '1.5rem' }}>
                      {onDrillDown ? (
                        <a 
                          href="#" 
                          onClick={(e) => {
                            e.preventDefault()
                            handleConceptClick('ingresos_financieros')
                          }}
                          className="text-decoration-none"
                          title="Ver transacciones de ingresos financieros"
                        >
                          Ingresos Financieros
                          <i className="ms-2 bi bi-arrow-right-circle" style={{ fontSize: '0.9em' }}></i>
                        </a>
                      ) : (
                        'Ingresos Financieros'
                      )}
                    </span>
                  </td>
                  <td className="text-end">{formatCurrency(reportData.otros_ingresos_egresos.ingresos_financieros)}</td>
                </tr>
                <tr>
                  <td>
                    <span style={{ paddingLeft: '1.5rem' }}>
                      {onDrillDown ? (
                        <a 
                          href="#" 
                          onClick={(e) => {
                            e.preventDefault()
                            handleConceptClick('gastos_financieros')
                          }}
                          className="text-decoration-none"
                          title="Ver transacciones de gastos financieros"
                        >
                          Gastos Financieros
                          <i className="ms-2 bi bi-arrow-right-circle" style={{ fontSize: '0.9em' }}></i>
                        </a>
                      ) : (
                        'Gastos Financieros'
                      )}
                    </span>
                  </td>
                  <td className="text-end">{formatCurrency(reportData.otros_ingresos_egresos.gastos_financieros)}</td>
                </tr>
                <tr>
                  <td>
                    <span style={{ paddingLeft: '1.5rem' }}>
                      {onDrillDown ? (
                        <a 
                          href="#" 
                          onClick={(e) => {
                            e.preventDefault()
                            handleConceptClick('otros_ingresos')
                          }}
                          className="text-decoration-none"
                          title="Ver otros ingresos"
                        >
                          Otros Ingresos
                          <i className="ms-2 bi bi-arrow-right-circle" style={{ fontSize: '0.9em' }}></i>
                        </a>
                      ) : (
                        'Otros Ingresos'
                      )}
                    </span>
                  </td>
                  <td className="text-end">{formatCurrency(reportData.otros_ingresos_egresos.otros_ingresos)}</td>
                </tr>
                <tr>
                  <td>
                    <span style={{ paddingLeft: '1.5rem' }}>
                      {onDrillDown ? (
                        <a 
                          href="#" 
                          onClick={(e) => {
                            e.preventDefault()
                            handleConceptClick('otros_egresos')
                          }}
                          className="text-decoration-none"
                          title="Ver otros egresos"
                        >
                          Otros Egresos
                          <i className="ms-2 bi bi-arrow-right-circle" style={{ fontSize: '0.9em' }}></i>
                        </a>
                      ) : (
                        'Otros Egresos'
                      )}
                    </span>
                  </td>
                  <td className="text-end">{formatCurrency(reportData.otros_ingresos_egresos.otros_egresos)}</td>
                </tr>
                <tr>
                  <td><strong>Utilidad Antes de Impuestos</strong></td>
                  <td className="text-end"><strong>{formatCurrency(reportData.utilidad_antes_impuestos)}</strong></td>
                </tr>
                <tr>
                  <td>Impuestos</td>
                  <td className="text-end">{formatCurrency(reportData.impuestos)}</td>
                </tr>
                <tr>
                  <td><strong>Utilidad Neta</strong></td>
                  <td className="text-end"><strong>{formatCurrency(reportData.utilidad_neta)}</strong></td>
                </tr>
              </tbody>
            </Table>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

export default EstadoResultadosReport